import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { type EditorContents, ensureSchemaVariants } from "./editorContents";
import { beginSeed, endSeed } from "./schemaSeeding";
import {
  pickIgnored,
  schemaDraftText,
  schemaIsCustom,
  seedDecision,
  type VariantEditTarget,
  variantSwitchEdits,
} from "./schemaVariants";
import type { SchemaDrafts } from "./stagingDrafts";
import {
  parseResourceId,
  type StagedId,
  type StagedResource,
  type StagedRole,
  type StagedSchema,
  schemaEditorId,
} from "./stagingModel";
import { findStoredSchema } from "./storedSchemas";

// The variant picker's orchestration (TTTL-179 gap 4): a dropped source
// auto-fills its SCHEMA slot — the schema already stored under the
// collection's name, or else the majority shape's schema as a draft — and
// the input editor's variant bar switches both the sample document and
// that draft to another shape — through a confirm when the user edited
// either.

/** A switch held back by manual edits, waiting on the confirm modal. */
export interface PendingVariantSwitch {
  index: number;
  edits: VariantEditTarget[];
}

/** What a drop puts into the stage's SCHEMA slot: the schema already
 *  stored under the collection's name, staged as-is (no draft, no star),
 *  or the introspected majority shape as an unsaved draft. */
export type SeedContent =
  | { kind: "stored"; schema: StagedResource }
  | { kind: "draft"; text: string };

/** A drop-time seed held back by the slot's custom draft, waiting on the
 *  replace-schema confirm modal. */
export interface PendingSeed {
  resource: StagedResource;
  seed: SeedContent;
}

/** A schema stage the slot belongs to. */
type SeedTarget = { role: StagedRole; id: StagedId };

// The moment-of-truth values the async seeding and the switch read — a
// live ref snapshot of the provider's current render.
interface VariantContext {
  editor: EditorContents;
  drafts: SchemaDrafts;
  schemas: StagedSchema[];
  sources: StagedResource[];
  sinks: StagedResource[];
  /** Attaches a stored schema to a stage's slot, leaving the just-dropped
   *  stage row selected. */
  attachSchema: (schema: StagedResource, target: SeedTarget) => void;
  /** Attaches AND selects the schema row — the confirm-replace path
   *  shows what replaced the draft. */
  stageSchemaFor: (schema: StagedResource, target: SeedTarget) => void;
}

/** Baseline map updater — what the auto-seeded draft last held per row. */
type SetBaselines = (
  fn: (prev: Record<string, string>) => Record<string, string>,
) => void;

// Writes one variant's schema into the source's SCHEMA slot draft and
// records the new baseline — the shared tail of seeding and switching.
function writeDraft(
  ctx: { drafts: SchemaDrafts; setBaselines: SetBaselines },
  rowId: string,
  name: string,
  text: string,
) {
  ctx.drafts.seedDraft(rowId, name, text);
  ctx.setBaselines((prev) => ({ ...prev, [rowId]: text }));
}

// Where the dropped stage sits NOW — null once it left staging while the
// async seed resolved or the confirm modal waited.
function stagedTarget(
  resource: StagedResource,
  ctx: VariantContext,
): SeedTarget | null {
  if (ctx.sources.some((s) => s.id === resource.id))
    return { role: "source", id: resource.id };
  if (ctx.sinks.some((s) => s.id === resource.id))
    return { role: "sink", id: resource.id };
  return null;
}

// What fills the slot: the schema already stored under the collection's
// name wins (see storedSchemas); otherwise the introspected majority
// shape becomes a draft. Null when there is neither (an empty sink
// introspects to nothing).
async function resolveSeed(
  queryClient: QueryClient,
  resource: StagedResource,
): Promise<SeedContent | null> {
  const stored = await findStoredSchema(
    queryClient,
    resource.name,
    parseResourceId(resource.id)?.serviceName,
  );
  if (stored) return { kind: "stored", schema: stored };
  const variants = await ensureSchemaVariants(queryClient, resource);
  const majority = variants?.[0];
  return majority ? { kind: "draft", text: schemaDraftText(majority) } : null;
}

// Puts the resolved seed into the stage's slot: a stored schema attaches
// as a saved row (selected only when it replaced a draft the user
// confirmed away), a draft writes into the slot with its baseline.
function applySeed(
  { resource, seed }: PendingSeed,
  target: SeedTarget,
  ctx: VariantContext,
  effects: { setBaselines: SetBaselines; select: boolean },
) {
  if (seed.kind === "stored") {
    const attach = effects.select ? ctx.stageSchemaFor : ctx.attachSchema;
    attach(seed.schema, target);
    return;
  }
  writeDraft(
    { drafts: ctx.drafts, setBaselines: effects.setBaselines },
    schemaEditorId(resource.id),
    resource.name,
    seed.text,
  );
}

// The dropped stage's auto schema (sources AND sinks — a destination's
// existing documents shape what a write should look like), once the seed
// resolved: into the stage's SCHEMA slot, a replace-confirm when the slot
// holds a custom draft, and a silent skip when the stage left staging or
// a staged schema already carries the collection's name.
function seedIfWanted(
  resource: StagedResource,
  seed: SeedContent | null,
  ctx: VariantContext,
  effects: { setBaselines: SetBaselines; ask: (pending: PendingSeed) => void },
) {
  if (!seed) return;
  const target = stagedTarget(resource, ctx);
  if (!target) return;
  const rowId = schemaEditorId(resource.id);
  const decision = seedDecision({
    existingDraft: ctx.drafts.schemaDrafts[rowId],
    stagedSchemaNames: ctx.schemas.map((s) => s.name),
    collectionName: resource.name,
  });
  if (decision === "skip") return;
  if (decision === "ask") {
    effects.ask({ resource, seed });
    return;
  }
  applySeed({ resource, seed }, target, ctx, {
    setBaselines: effects.setBaselines,
    select: false,
  });
}

// The drop modal's Replace: applies the parked seed — unless the stage
// left staging while the modal waited. The replacement shows itself: the
// user just gave up a draft for it.
function applyPendingSeed(
  pending: PendingSeed,
  ctx: VariantContext,
  setBaselines: SetBaselines,
) {
  const target = stagedTarget(pending.resource, ctx);
  if (!target) return;
  applySeed(pending, target, ctx, { setBaselines, select: true });
}

// Whether the active source's slot holds a custom schema — the footer
// trigger reads "Custom variant" with nothing selected then.
function computeVariantIsCustom(
  ctx: Pick<VariantContext, "editor" | "drafts">,
  baselines: Record<string, string>,
): boolean {
  const active = ctx.editor.sourceVariants;
  if (!active) return false;
  const rowId = schemaEditorId(active.resourceId);
  return schemaIsCustom(
    ctx.drafts.schemaDrafts[rowId],
    baselines[rowId] ?? null,
  );
}

// Applies a switch: the sample document always; the SCHEMA slot draft
// only for a STAGED source whose slot holds one — a custom draft was
// confirmed replaceable by the modal (variantSwitchEdits flags it), an
// empty slot (discarded, or covered by a staged schema) stays empty, and
// tree previews swap just the sample.
function applySwitch(
  index: number,
  ctx: VariantContext,
  setBaselines: SetBaselines,
) {
  const active = ctx.editor.sourceVariants;
  if (!active) return;
  ctx.editor.switchVariantSample(index);
  const staged = ctx.sources.find((s) => s.id === active.resourceId);
  const variant = active.variants[index];
  if (!staged || !variant) return;
  const rowId = schemaEditorId(staged.id);
  if ((ctx.drafts.schemaDrafts[rowId] ?? "").trim() === "") return;
  writeDraft(
    { drafts: ctx.drafts, setBaselines },
    rowId,
    staged.name,
    schemaDraftText(variant),
  );
}

// What the bar's pick should do: nothing (no-op pick), apply right away,
// or park behind the confirm modal naming the edited parts.
function switchDecision(
  index: number,
  ctx: VariantContext,
  baselines: Record<string, string>,
): "ignore" | "apply" | PendingVariantSwitch {
  const active = ctx.editor.sourceVariants;
  if (!active) return "ignore";
  const custom = computeVariantIsCustom(ctx, baselines);
  if (pickIgnored(index, active.activeIndex, custom)) return "ignore";
  const rowId = schemaEditorId(active.resourceId);
  const edits = variantSwitchEdits({
    inputDocument: ctx.editor.inputDocument,
    inputOrigin: ctx.editor.inputOrigin,
    schemaDraft: ctx.drafts.schemaDrafts[rowId],
    schemaBaseline: baselines[rowId] ?? null,
  });
  return edits.length === 0 ? "apply" : { index, edits };
}

// The drop-time half of the feature: resolve-and-seed (stored schema, else
// introspection) with the replace-schema modal riding on pendingSeed. A
// newer drop replaces an unanswered one.
function useSeedFlow(
  live: { current: VariantContext },
  setBaselines: SetBaselines,
) {
  const queryClient = useQueryClient();
  const [pendingSeed, setPendingSeed] = useState<PendingSeed | null>(null);
  // The stages whose seed is still resolving — their SCHEMA slots show
  // "in progress" (schemaSeeding.ts) until the seed lands, is parked on
  // the replace modal, is skipped, or fails.
  const [seedingStageIds, setSeedingStageIds] = useState<string[]>([]);
  // Overlapping seeds for one stage (unstage + re-stage while the first
  // lookup is pending): only the latest one's completion clears the wait.
  const seedLedger = useRef(new Map<string, number>());

  const seedStageSchemaDraft = useCallback(
    (resource: StagedResource) => {
      const token = beginSeed(seedLedger.current, resource.id);
      setSeedingStageIds((prev) =>
        prev.includes(resource.id) ? prev : [...prev, resource.id],
      );
      resolveSeed(queryClient, resource)
        .then((seed) =>
          seedIfWanted(resource, seed, live.current, {
            setBaselines,
            ask: setPendingSeed,
          }),
        )
        .catch(() => {
          // Introspection failed — the drop already surfaced what it could.
        })
        .finally(() => {
          if (endSeed(seedLedger.current, resource.id, token)) {
            setSeedingStageIds((prev) =>
              prev.filter((id) => id !== resource.id),
            );
          }
        });
    },
    [queryClient, live, setBaselines],
  );

  const confirmSeedReplace = useCallback(() => {
    if (pendingSeed) applyPendingSeed(pendingSeed, live.current, setBaselines);
    setPendingSeed(null);
  }, [pendingSeed, live, setBaselines]);

  const cancelSeedReplace = useCallback(() => setPendingSeed(null), []);

  return {
    pendingSeed,
    seedingStageIds,
    seedStageSchemaDraft,
    confirmSeedReplace,
    cancelSeedReplace,
  };
}

export function useVariantSwitch(options: VariantContext) {
  // Async work below (introspection, confirm modal) must see the values
  // of ITS moment, not of the render that created the callback.
  const live = useRef(options);
  live.current = options;

  const [seedBaselines, setSeedBaselines] = useState<Record<string, string>>(
    {},
  );
  const [pendingVariantSwitch, setPendingVariantSwitch] =
    useState<PendingVariantSwitch | null>(null);
  const baselinesRef = useRef(seedBaselines);
  baselinesRef.current = seedBaselines;

  const {
    pendingSeed,
    seedStageSchemaDraft,
    confirmSeedReplace,
    cancelSeedReplace,
    seedingStageIds,
  } = useSeedFlow(live, setSeedBaselines);

  const requestVariantSwitch = useCallback((index: number) => {
    const decision = switchDecision(index, live.current, baselinesRef.current);
    if (decision === "ignore") return;
    if (decision === "apply") {
      applySwitch(index, live.current, setSeedBaselines);
      return;
    }
    setPendingVariantSwitch(decision);
  }, []);

  const confirmVariantSwitch = useCallback(() => {
    if (pendingVariantSwitch) {
      applySwitch(pendingVariantSwitch.index, live.current, setSeedBaselines);
    }
    setPendingVariantSwitch(null);
  }, [pendingVariantSwitch]);

  const cancelVariantSwitch = useCallback(
    () => setPendingVariantSwitch(null),
    [],
  );

  const variantIsCustom = computeVariantIsCustom(options, seedBaselines);

  return useMemo(
    () => ({
      seedStageSchemaDraft,
      requestVariantSwitch,
      pendingVariantSwitch,
      confirmVariantSwitch,
      cancelVariantSwitch,
      pendingSeed,
      seedingStageIds,
      confirmSeedReplace,
      cancelSeedReplace,
      variantIsCustom,
    }),
    [
      seedStageSchemaDraft,
      requestVariantSwitch,
      pendingVariantSwitch,
      confirmVariantSwitch,
      cancelVariantSwitch,
      pendingSeed,
      seedingStageIds,
      confirmSeedReplace,
      cancelSeedReplace,
      variantIsCustom,
    ],
  );
}
